import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/CookieConsent";
import Home from "@/pages/Home";

// Route-level code splitting: every page except the landing (Home) loads on demand.
const Market = lazy(() => import("@/pages/Market"));
const Prices = lazy(() => import("@/pages/Prices"));
const Directory = lazy(() => import("@/pages/Directory"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const Tools = lazy(() => import("@/pages/Tools"));
const CostCalculator = lazy(() => import("@/pages/tools/CostCalculator"));
const Alerts = lazy(() => import("@/pages/tools/Alerts"));
const Packaging = lazy(() => import("@/pages/tools/Packaging"));
const Inspection = lazy(() => import("@/pages/tools/Inspection"));
const QuoteThread = lazy(() => import("@/pages/QuoteThread"));
const ConfirmAlert = lazy(() => import("@/pages/ConfirmAlert"));
const ManageAlerts = lazy(() => import("@/pages/ManageAlerts"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ConfirmEmail = lazy(() => import("@/pages/ConfirmEmail"));
const Legal = lazy(() => import("@/pages/Legal"));
const LotDetail = lazy(() => import("@/pages/LotDetail"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const CompleteProfile = lazy(() => import("@/pages/CompleteProfile"));
const Overview = lazy(() => import("@/pages/dashboard/Overview"));
const CompanyPage = lazy(() => import("@/pages/dashboard/Company"));
const Verification = lazy(() => import("@/pages/dashboard/Verification"));
const Settings = lazy(() => import("@/pages/dashboard/Settings"));
const MyLots = lazy(() => import("@/pages/dashboard/MyLots"));
const MyOffers = lazy(() => import("@/pages/dashboard/MyOffers"));
const NegotiationCenter = lazy(() => import("@/pages/dashboard/NegotiationCenter"));
const AdminMarket = lazy(() => import("@/pages/dashboard/admin/AdminMarket"));
const AdminTools = lazy(() => import("@/pages/dashboard/admin/AdminTools"));
const AdminBlog = lazy(() => import("@/pages/dashboard/admin/AdminBlog"));
const AdminOverview = lazy(() => import("@/pages/dashboard/admin/AdminOverview"));
const AdminUsers = lazy(() => import("@/pages/dashboard/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("@/pages/dashboard/admin/AdminUserDetail"));
const AdminLots = lazy(() => import("@/pages/dashboard/admin/AdminLots"));
const AdminLotDetail = lazy(() => import("@/pages/dashboard/admin/AdminLotDetail"));
const AdminDeals = lazy(() => import("@/pages/dashboard/admin/AdminDeals"));
const AdminInvoices = lazy(() => import("@/pages/dashboard/admin/AdminInvoices"));
const AdminSettings = lazy(() => import("@/pages/dashboard/admin/AdminSettings"));
const AdminFaq = lazy(() => import("@/pages/dashboard/admin/AdminFaq"));
const Faq = lazy(() => import("@/pages/Faq"));
const Contact = lazy(() => import("@/pages/Contact"));
const AdminContact = lazy(() => import("@/pages/dashboard/admin/AdminContact"));
const MillDetail = lazy(() => import("@/pages/MillDetail"));
const DirectoryProfile = lazy(() => import("@/pages/dashboard/DirectoryProfile"));
const SellerQuotes = lazy(() => import("@/pages/dashboard/SellerQuotes"));
const MyQuotes = lazy(() => import("@/pages/dashboard/MyQuotes"));
const MyInvoices = lazy(() => import("@/pages/dashboard/MyInvoices"));
const DealsHistory = lazy(() => import("@/pages/dashboard/DealsHistory"));

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/20" data-testid="route-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  return (
    <Suspense fallback={<RouteFallback />}>
      {location.hash?.includes("session_id=") ? (
        <AuthCallback />
      ) : (
        <Routes>
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/marche" element={<Market />} />
          <Route path="/marche/lot/:id" element={<LotDetail />} />
          <Route path="/marche/:section" element={<Market />} />
          <Route path="/prix" element={<Prices />} />
          <Route path="/annuaire" element={<Directory />} />
          <Route path="/annuaire/:id" element={<MillDetail />} />
          <Route path="/outils" element={<Tools />} />
          <Route path="/outils/calculatrice" element={<CostCalculator />} />
          <Route path="/outils/alertes" element={<Alerts />} />
          <Route path="/outils/conditionnement" element={<Packaging />} />
          <Route path="/outils/inspection" element={<Inspection />} />
          <Route path="/devis/:token" element={<QuoteThread />} />
          <Route path="/alertes/confirmer" element={<ConfirmAlert />} />
          <Route path="/alertes/gerer" element={<ManageAlerts />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/inscription" element={<Register />} />
          <Route path="/confirmer-email" element={<ConfirmEmail />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/legal/:type" element={<Legal />} />

          <Route path="/completer-profil" element={<ProtectedRoute allowIncomplete><CompleteProfile /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Overview /></ProtectedRoute>} />
          <Route path="/dashboard/lots" element={<ProtectedRoute><MyLots /></ProtectedRoute>} />
          <Route path="/dashboard/offres" element={<ProtectedRoute><MyOffers /></ProtectedRoute>} />
          <Route path="/dashboard/messages" element={<ProtectedRoute><NegotiationCenter /></ProtectedRoute>} />
          <Route path="/dashboard/deals" element={<ProtectedRoute><DealsHistory /></ProtectedRoute>} />
          <Route path="/dashboard/mes-devis" element={<ProtectedRoute><MyQuotes /></ProtectedRoute>} />
          <Route path="/dashboard/mes-factures" element={<ProtectedRoute><MyInvoices /></ProtectedRoute>} />
          <Route path="/dashboard/entreprise" element={<ProtectedRoute><CompanyPage /></ProtectedRoute>} />
          <Route path="/dashboard/verification" element={<ProtectedRoute><Verification /></ProtectedRoute>} />
          <Route path="/dashboard/parametres" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/dashboard/fiche-annuaire" element={<ProtectedRoute sellerOnly><DirectoryProfile /></ProtectedRoute>} />
          <Route path="/dashboard/devis-conditionnement" element={<ProtectedRoute sellerOnly><SellerQuotes /></ProtectedRoute>} />
          <Route path="/dashboard/admin" element={<ProtectedRoute adminOnly><AdminOverview /></ProtectedRoute>} />
          <Route path="/dashboard/admin/utilisateurs" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
          <Route path="/dashboard/admin/utilisateurs/:id" element={<ProtectedRoute adminOnly><AdminUserDetail /></ProtectedRoute>} />
          <Route path="/dashboard/admin/lots" element={<ProtectedRoute adminOnly><AdminLots /></ProtectedRoute>} />
          <Route path="/dashboard/admin/lots/:id" element={<ProtectedRoute adminOnly><AdminLotDetail /></ProtectedRoute>} />
          <Route path="/dashboard/admin/operations" element={<ProtectedRoute adminOnly><AdminDeals /></ProtectedRoute>} />
          <Route path="/dashboard/admin/factures" element={<ProtectedRoute adminOnly><AdminInvoices /></ProtectedRoute>} />
          <Route path="/dashboard/admin/reglages" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
          <Route path="/dashboard/admin/marche" element={<ProtectedRoute adminOnly><AdminMarket /></ProtectedRoute>} />
          <Route path="/dashboard/admin/outils" element={<ProtectedRoute adminOnly><AdminTools /></ProtectedRoute>} />
          <Route path="/dashboard/admin/blog" element={<ProtectedRoute adminOnly><AdminBlog /></ProtectedRoute>} />
          <Route path="/dashboard/admin/faq" element={<ProtectedRoute adminOnly><AdminFaq /></ProtectedRoute>} />
          <Route path="/dashboard/admin/contact" element={<ProtectedRoute adminOnly><AdminContact /></ProtectedRoute>} />

          <Route path="*" element={<Layout><Home /></Layout>} />
        </Routes>
      )}
    </Suspense>
  );
}

function App() {
  return (
    <AppProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </AppProvider>
  );
}

export default App;
